import React, { useState, useEffect, useCallback } from 'react';
import { fetchLowStock } from '../utils/api';
import { Media } from '../utils/types';

interface StockLocation {
  location_id: string;
  location_name: string;
  qty: number;
  min_qty: number;
  rack_code?: string;
  rack_level?: string;
  side?: string;
  box_code?: string;
}

interface ProductItem {
  product_id: string;
  product_name: string;
  stock_by_location: StockLocation[];
  media: Media[];
}

export default function LowStockList() {
  const [stocks, setStocks] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLowStock = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { products } = await fetchLowStock();

      // Group results into product -> stock_by_location
      const grouped: Record<string, ProductItem> = {};

      products.forEach((item: any) => {
        if (!grouped[item.product_id]) {
          grouped[item.product_id] = {
            product_id: item.product_id,
            product_name: item.product_name,
            stock_by_location: [],
            media: item.media || [],
          };
        }

        grouped[item.product_id].stock_by_location.push({
          location_id: item.location_id,
          location_name: item.location_name,
          qty: Number(item.qty),
          min_qty: Number(item.min_qty),
          rack_code: item.rack_code,
          rack_level: item.rack_level,
          side: item.side,
          box_code: item.box_code,
        });
      });

      setStocks(Object.values(grouped));

    } catch (err: any) {
      setError(err.message);
      setStocks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLowStock();
  }, [loadLowStock]);

  if (loading) return <div>Loading low stock items...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div>

      {stocks.length === 0 && <p>No low stock items found.</p>}


      {stocks.map(product => (
        <div key={product.product_id} className='mb-4 border border-gray-300 rounded p-4'>
          <div className="flex items-center">
            <div className='w-1/6'>
              <img
                src={product.media.length > 0 ? (product.media[0].preview_url || product.media[0].original_url) : window.APP_CONFIG?.PUBLIC_URL + "/placeholder.jpg"}
                alt={product.product_name}
                className="w-8 h-8 m-2"
              />
            </div>
            <div className='w-5/6 text-xs md:text-sm'>
              <strong>PID#{product.product_id} - {product.product_name}</strong>
            </div>
          </div>
          <div>
            <div>
              <div className='grid grid-cols-4 gap-2 md:grid-cols-8 font-bold border-b border-gray-300 m-2 p-2 text-pink-900 text-xs md:text-sm'>
                <div className='mr-2'>Location</div>
                <div>Qty</div>
                <div>Min Qty</div>
                <div>Rack Code</div>
                <div>Rack Level</div>
                <div>Side</div>
                <div>Box Code</div>
              </div>
            </div>
            <div>
              {product.stock_by_location.map(loc => (
                <div
                  key={`${product.product_id}-${loc.location_id}-${loc.rack_code}`}
                  // style={{
                  //   backgroundColor: loc.qty < loc.min_qty ? '#f8e4e4ff' : 'transparent',
                  // }}
                  className='grid grid-cols-4 gap-2 md:grid-cols-8 border-b border-gray-300 m-2 p-2 bg-pink-100 hover:bg-pink-300 rounded-xl cursor-pointer text-xs md:text-sm'
                >
                  <div className='mr-2'>{loc.location_name}</div>
                  <div className='text-xl font-bold text-red-900'>{loc.qty}</div>
                  <div>{loc.min_qty}</div>
                  <div>{loc.rack_code}</div>
                  <div>{loc.rack_level}</div>
                  <div>{loc.side}</div>
                  <div>{loc.box_code}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
